import type { Axios } from "axios";
import { function as function_, taskEither } from "fp-ts";

import { NotificationServicePorts } from "../../model";

import { URLS } from "./consts";
import { GetMany, GetPaginated, MarkAllAsRead, MarkAsRead } from "./ios";
import { mapDtoToEntity } from "./mappers";

import { Request, Response } from "~/shared/api";
import { AuthenticationError, NotFoundError, UnexpectedError } from "~/shared/error";
import { Pagination } from "~/shared/zod";

class ApiProvider extends NotificationServicePorts.Provider {
  private readonly axios: Axios;

  constructor(axios: Axios) {
    super();
    this.axios = axios;
  }

  override markAsRead({
    id,
  }: NotificationServicePorts.ProviderIos.MarkAsRead.In): taskEither.TaskEither<
    UnexpectedError | AuthenticationError | NotFoundError,
    void
  > {
    return function_.pipe(
      Response.process(() => this.axios.post(URLS.markAsRead(id)), MarkAsRead.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(void 0);

        if (status === 401) return taskEither.left(new AuthenticationError());

        if (status === 404) return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override markAllAsRead(): taskEither.TaskEither<UnexpectedError | AuthenticationError, void> {
    return function_.pipe(
      Response.process(() => this.axios.post(URLS.markAllAsRead), MarkAllAsRead.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(void 0);

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override getMany(): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    NotificationServicePorts.ProviderIos.GetMany.Out
  > {
    return function_.pipe(
      Response.process(() => this.axios.get(URLS.getMany), GetMany.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(response.body.data.map(mapDtoToEntity));

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override getPaginated(
    params: NotificationServicePorts.ProviderIos.GetPaginated.In,
  ): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    NotificationServicePorts.ProviderIos.GetPaginated.Out
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.post(
            URLS.getPaginated,
            Request.ListParams.mapListParams(params, GetPaginated.MAP_OF_FIELDS_TO_SORT_BY),
          ),
        GetPaginated.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) {
          const {
            data: { items, totalCount, unreadCount },
          } = response.body;
          const {
            success,
            data: pageMeta,
            error,
          } = Pagination.Meta.zSchema.safeParse({
            options: params.pagination,
            totalItems: totalCount,
          });

          if (!success) return taskEither.left(new UnexpectedError(error));

          return taskEither.right({
            items: items.map(mapDtoToEntity),
            meta: pageMeta,
            more: {
              totalItemsRead: totalCount - unreadCount,
            },
          } satisfies NotificationServicePorts.ProviderIos.GetPaginated.Out);
        }

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }
}
export { ApiProvider };
